import clsx from 'clsx';
import round from 'lodash/round';

import { Currency } from '@cast/shared-types';
import { formatCurrency } from '@cast/utils';

import { NumberFormat } from 'components/number/NumberFormat';

const currencyToSymbol: Record<Currency, string> = {
  USD: '$',
  EUR: '€',
};

export type MoneyProps = {
  amount?: number;
  currency?: Currency;
  precision?: number;
  fixed?: number;
  raw?: boolean;
  dashesIfUndefined?: boolean;
  className?: string;
  testId?: string;
  suffix?: string;
};

type GetValueArgs = {
  amount?: number;
  precision?: number;
  fixed?: number;
  raw?: boolean;
};

const getValue = ({ amount, precision = 2, fixed = 2, raw }: GetValueArgs) => {
  const roundedAmount = round(amount!, precision);
  return raw
    ? roundedAmount
    : formatCurrency(roundedAmount, {
        maximumFractionDigits: fixed,
      });
};

export const Money = ({
  amount,
  currency = 'USD',
  precision,
  fixed,
  raw = false,
  dashesIfUndefined,
  className,
  testId,
  suffix,
}: MoneyProps) => {
  const autoPositionDecimalPoint = Math.abs(amount || 0) > 1000 ? 0 : 2; // if we're dealing with thousands, we likely don't care about seeing cents.
  const decimalPoint = fixed || autoPositionDecimalPoint; // specified value has priority over auto-positioning
  const currencyPrefix = currencyToSymbol[currency] ?? currency;

  if (!isFinite(amount!) && dashesIfUndefined) {
    return (
      <span className={clsx('Money-root', className)} data-testid={testId}>
        {currencyPrefix}--.--
      </span>
    );
  }

  return (
    <NumberFormat
      className={clsx('Money-root', className)}
      thousandSeparator
      displayType="text"
      prefix={currencyPrefix}
      value={getValue({ amount, precision, fixed: decimalPoint, raw })}
      data-testid={testId}
      suffix={suffix}
    />
  );
};
